import React, {
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import DisplayAds from 'bdvp-common/src/utils/displayAds';
import { getMediaPath } from 'bdvp-common/src/utils';
import Ad from 'bdvp-common/src/components/Ad';
import Head from 'bdvp-common/src/components/Head';
import ContentCardsGroup from 'bdvp-common/src/components/ContentCardsGroup';
import NotFound from 'bdvp-common/src/components/NotFound';
import Error from 'bdvp-common/src/components/Error';
import PageTemplate from 'bdvp-common/src/components/PageTemplate';
import PageHeading from 'bdvp-common/src/components/PageHeading';
import NotificationRenderer from 'bdvp-common/src/components/NotificationRenderer';
import {
    useDidUpdate,
    usePagination,
    useMainContainerQuery,
} from 'bdvp-common/src/hooks';

import { Tracking } from 'bdvp-tracking';

import Notification from './Notification';

const LandingPage = ({
    basePath,
    config,
    data,
    dictionary,
    error,
    fetchMoreData,
    history,
    lang,
    loading,
    loadingMore,
    topicId,
}) => {

    const displayAdConfig = _get(config, 'displayAd', {});
    const bannerAdConfig = DisplayAds.lookupDisplayAd(displayAdConfig, 'banner-ad');
    const title = _get(data, 'topicPlayList.title', '');
    const list = _get(data, 'topicPlayList.mediaPlayback', []);
    const hasMore = _get(data, 'topicPlayList.hasMore', false);

    useEffect(() => {

        window.scrollTo(0, 0);
        DisplayAds.init(displayAdConfig);
        Tracking.trackLandingPageView();

        return () => {

            DisplayAds.destroySlots(displayAdConfig);

        };

    }, []);

    const {
        limit,
        sizes,
    } = useMainContainerQuery(
        [
            'XL',
            'LG',
            'MD',
            'SM',
            'XS',
        ],
        [
            {
                limit: 20,
                sizes: {
                    xs: 20,
                },
            },
            {
                limit: 16,
                sizes: {
                    xs: 25,
                },
            },
            {
                limit: 15,
                sizes: {
                    xs: 33.33,
                },
            },
            {
                limit: 12,
                sizes: {
                    xs: 50,
                },
            },
            {
                limit: 6,
                sizes: {
                    xs: 100,
                },
            },
        ],
        {
            limit: 20,
            sizes: {
                xs: 100,
                sm: 50,
                md: 33.33,
                lg: 25,
                xl: 20,
            },
        }
    );

    const {
        paginatedList,
        hasMoreItems,
        getMoreItems,
        resetPagination,
    } = usePagination({
        list,
        limit,
        hasMore,
        fetchMore: fetchMoreData,
        isLoading: loading,
    });

    useDidUpdate(() => {

        window.scrollTo(0, 0);
        DisplayAds.updatePageTargeting(displayAdConfig);
        resetPagination();

    }, [
        topicId,
        displayAdConfig,
    ]);

    const onItemClick = (event, item, index) => {

        event.preventDefault();
        const mediaPath = getMediaPath(item);
        history.push({
            pathname: `/${mediaPath}`,
            search: `?t=${topicId}`,
        });

        Tracking.trackLandingPageOnItemClick(event, index);

    };

    const seeMoreBtnHandler = () => {

        getMoreItems();
        Tracking.trackLandingPageSeeMoreButtonClick();

    };

    // TODO: We should get a better indicator of a 404 from the service
    if (title === 'Not Found') {

        return <NotFound />;

    }

    if (error) {

        return <Error />;

    }

    return (
        <>
            <Head title={`${title} Videos`} />
            <Ad
                pageTargeting={DisplayAds.updatePageTargetingPageIdWithTopicId(displayAdConfig, topicId)}
                isResponsive={_get(displayAdConfig, 'responsive')}
                adSlots={_get(bannerAdConfig, 'slots')}
                targeting={_get(bannerAdConfig, 'targeting')}
            />
            <NotificationRenderer
                config={config}
                topicId={topicId}
                render={(id, contentConfig) => (
                    <Notification id={id} contentConfig={contentConfig} />
                )}
            />
            <PageTemplate
                header={<PageHeading title={title} isLoading={loading} />}
                body={
                    <ContentCardsGroup
                        basePath={basePath}
                        list={paginatedList}
                        handleItemClick={onItemClick}
                        config={config}
                        lang={lang}
                        limit={limit}
                        orientation={'portrait'}
                        loadingMore={loadingMore}
                        dictionary={dictionary}
                        loadMoreButtonHandler={seeMoreBtnHandler}
                        hasMore={hasMoreItems}
                        isLoading={loading || paginatedList?.length === 0}
                        sizes={sizes}
                    />
                }
            />
        </>
    );

};

LandingPage.propTypes = {
    basePath: PropTypes.string.isRequired,
    config: PropTypes.shape().isRequired,
    dictionary: PropTypes.shape().isRequired,
    fetchMoreData: PropTypes.func.isRequired,
    history: PropTypes.shape().isRequired,
    lang: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    loadingMore: PropTypes.bool.isRequired,
    topicId: PropTypes.string.isRequired,
    data: PropTypes.shape(),
    error: PropTypes.shape(),
    isFilterActive: PropTypes.bool,
};

LandingPage.defaultProps = {
    data: {},
    isFilterActive: false,
};

export default LandingPage;
